import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "courseSelect",
    "calendarContainer",
    "calendar",
    "monthYear",
    "selectedDate",
    "startDateInput",
    "endDateInput",
    "timeSlotStart",
    "timeSlotEnd",
    "dateInfoContainer",
  ];

  static values = {
    sessionsUrl: String,
  };

  connect() {
    this.currentDate = new Date();
    this.sessions = [];
    this.selectedDateStr = null;

    // Check if there's already a selected date from a previous submission
    this.initializeFromExistingData();
    this.initializeCalendar();

    // Load sessions if a course is already selected
    if (this.courseSelectTarget.value) {
      this.loadSessions();
    }
  }

  initializeFromExistingData() {
    // Check if start_date already has a value (coming back to page or after error)
    const startDateValue = this.startDateInputTarget.value;

    if (startDateValue && startDateValue.trim() !== "") {
      // Extract date part (YYYY-MM-DD) from the datetime value
      // Handle formats: "2025-12-10T08:00", "2025-12-10 08:00", "2025-12-10 08:00:00 +0100"
      const dateMatch = startDateValue.match(/(\d{4}-\d{2}-\d{2})/);

      if (dateMatch) {
        const datePart = dateMatch[1];
        this.selectedDateStr = datePart;

        // Set the calendar to the month of the selected date
        const [year, month] = datePart.split("-").map(Number);
        this.currentDate = new Date(year, month - 1, 1);

        // Show the date info container
        this.showExistingDateInfo(
          startDateValue,
          this.endDateInputTarget.value,
        );
      }
    }
  }

  showExistingDateInfo(startDate, endDate) {
    if (!startDate) return;

    // Extract date and time using regex to handle all formats
    const dateMatch = startDate.match(/^(\d{4}-\d{2}-\d{2})/);
    const startTimeMatch = startDate.match(/(\d{2}:\d{2})/);
    const endTimeMatch = endDate ? endDate.match(/(\d{2}:\d{2})/) : null;

    const datePart = dateMatch ? dateMatch[1] : startDate.substring(0, 10);
    const startTime = startTimeMatch ? startTimeMatch[1] : "08:00";
    const endTime = endTimeMatch ? endTimeMatch[1] : "09:00";

    const date = new Date(datePart);
    if (this.hasSelectedDateTarget) {
      this.selectedDateTarget.textContent = date.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    if (this.hasTimeSlotStartTarget) {
      this.timeSlotStartTarget.textContent = startTime.substring(0, 5);
    }
    if (this.hasTimeSlotEndTarget) {
      this.timeSlotEndTarget.textContent = endTime.substring(0, 5);
    }
    if (this.hasDateInfoContainerTarget) {
      this.dateInfoContainerTarget.classList.remove("hidden");
    }
  }

  initializeCalendar() {
    this.updateMonthYear();
    this.renderCalendar();
  }

  updateMonthYear() {
    const monthNames = [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre",
    ];
    this.monthYearTarget.textContent = `${
      monthNames[this.currentDate.getMonth()]
    } ${this.currentDate.getFullYear()}`;
  }

  renderCalendar() {
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let firstDayOfWeek = firstDay.getDay();
    firstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    this.calendarTarget.innerHTML = "";

    // Empty days before first day of month
    for (let i = 0; i < firstDayOfWeek; i++) {
      const emptyDay = document.createElement("div");
      emptyDay.classList.add("p-1", "h-8", "text-center");
      this.calendarTarget.appendChild(emptyDay);
    }

    // Days of month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      const dayElement = document.createElement("div");
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(
        day,
      ).padStart(2, "0")}`;

      // Find session for this date
      const session = this.sessions.find(
        (s) => s.attributes.session_date === dateStr,
      );

      // Check if this date is the selected one
      const isSelected = this.selectedDateStr === dateStr;

      dayElement.textContent = day;
      dayElement.classList.add("p-1", "h-8", "text-center", "rounded");
      dayElement.dataset.date = dateStr;
      dayElement.dataset.action = "click->absence-calendar#selectDate";

      if (session) {
        if (isSelected) {
          // Selected session - dark green
          dayElement.classList.add(
            "bg-cta",
            "text-white",
            "cursor-pointer",
            "font-medium",
          );
        } else {
          // Available session - light green
          dayElement.classList.add(
            "bg-cta-shared",
            "hover:bg-cta-shared-hover",
            // Its own text colour: in dark mode the inherited white was unreadable.
            "text-cta-darker",
            "cursor-pointer",
            "font-medium",
          );
        }
      } else {
        if (isSelected) {
          // Selected day without session - still show as selected
          dayElement.classList.add("bg-cta", "text-white", "cursor-pointer");
        } else {
          dayElement.classList.add(
            "text-gray-400",
            "hover:text-gray-700",
            "cursor-pointer",
          );
        }
      }

      // Highlight today
      if (
        day === new Date().getDate() &&
        month === new Date().getMonth() &&
        year === new Date().getFullYear()
      ) {
        dayElement.classList.add("border", "border-blue-500");
      }

      this.calendarTarget.appendChild(dayElement);
    }
  }

  previousMonth() {
    this.currentDate = new Date(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth() - 1,
      1,
    );
    this.updateMonthYear();
    this.loadSessions();
  }

  nextMonth() {
    this.currentDate = new Date(
      this.currentDate.getFullYear(),
      this.currentDate.getMonth() + 1,
      1,
    );
    this.updateMonthYear();
    this.loadSessions();
  }

  loadSessions() {
    const courseId = this.courseSelectTarget.value;
    if (!courseId) {
      this.sessions = [];
      this.renderCalendar();
      return;
    }

    if (!this.hasSessionsUrlValue) {
      console.error("Sessions URL is not configured");
      return;
    }

    const year = this.currentDate.getFullYear();
    const month = String(this.currentDate.getMonth() + 1).padStart(2, "0");
    const start_date = `${year}-${month}-01`;
    const end_date = `${year}-${month}-${new Date(
      year,
      this.currentDate.getMonth() + 1,
      0,
    ).getDate()}`;

    let baseUrl = this.sessionsUrlValue
      .replace(":id", courseId)
      .replace("course_id=%3Aid", `course_id=${courseId}`);
    const separator = baseUrl.includes("?") ? "&" : "?";
    const url = `${baseUrl}${separator}start_date=${start_date}&end_date=${end_date}`;

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        this.sessions = data.data || [];
        this.renderCalendar();
      })
      .catch((error) => {
        console.error("Error loading sessions:", error);
        this.sessions = [];
        this.renderCalendar();
      });
  }

  selectDate(event) {
    const dateStr = event.currentTarget.dataset.date;
    if (!dateStr) return;

    // Find session for this date
    const session = this.sessions.find(
      (s) => s.attributes.session_date === dateStr,
    );

    let startTime = "08:00";
    let endTime = "09:00";

    if (session) {
      // Extract times from session
      const startTimeParts = session.attributes.start_time
        .split("T")[1]
        .split(":");
      const endTimeParts = session.attributes.end_time.split("T")[1].split(":");
      startTime = `${startTimeParts[0]}:${startTimeParts[1]}`;
      endTime = `${endTimeParts[0]}:${endTimeParts[1]}`;
    }

    // Update selected date
    this.selectedDateStr = dateStr;

    // Set the date inputs
    this.startDateInputTarget.value = `${dateStr}T${startTime}`;
    this.endDateInputTarget.value = `${dateStr}T${endTime}`;

    // Update date info display
    const date = new Date(dateStr);
    if (this.hasSelectedDateTarget) {
      this.selectedDateTarget.textContent = date.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
    }
    if (this.hasTimeSlotStartTarget) {
      this.timeSlotStartTarget.textContent = startTime;
    }
    if (this.hasTimeSlotEndTarget) {
      this.timeSlotEndTarget.textContent = endTime;
    }
    if (this.hasDateInfoContainerTarget) {
      this.dateInfoContainerTarget.classList.remove("hidden");
    }

    // Re-render calendar to update selection styling
    this.renderCalendar();
  }
}
